import dayjs from 'dayjs';
import React, { useState } from 'react';

import { Button, Modal } from '@/components';
import { SelectInput } from '@/components/select-input/SelectInput';
import { TimeInput } from '@/components/text-input';
import { useTranslation } from 'react-i18next';
import { PlatformIcon } from '@/utils/platformIcon';
import { useCollaborationUpdate } from '@/hooks/influencer';
import { useQueryClient } from '@tanstack/react-query';
import styles from './ChangeDatesModal.module.css';

interface ChooseDateModalProps {
  beat: Api.Beat;
  collabId: string;
  onUpdate: () => void;
  onCancel: () => void;
}

function ChangeDatesModal({ collabId, beat, onUpdate, onCancel }: ChooseDateModalProps) {
  const [selectedDate, setSelectedDate] = useState<string | null>(beat.date_publish || null);

  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutate, isLoading } = useCollaborationUpdate({
    chosenPublishDates: [{ beat_id: beat.beat_id, date_publish: selectedDate as string }],
    collaborationId: collabId,
    onSuccess: (data: TypeAny) => {
      if (data?.code === 'COLLABORATION_UPDATED' && 'collaboration_id' in data) {
        onUpdate();
        onCancel();

        const key = `influencer-collaboration-${collabId}`;
        queryClient.setQueryData([key, null], (prev: TypeAny) => {
          if (prev) {
            const updatedCollab = prev as Api.Influencer.Collaboration;
            updatedCollab.beats = data.beats;
            return updatedCollab;
          }
          return prev;
        });
      }
    }
  });

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const parsed = dayjs(date);
      const newDate = selectedDate
        ? parsed.set('hour', dayjs(selectedDate).hour()).set('minute', dayjs(selectedDate).minute())
        : parsed.startOf('day');

      setSelectedDate(newDate.toISOString());
    } else {
      setSelectedDate(null);
    }
  };

  const handleTimeChange = (timeString: string) => {
    const [hourStr, minuteStr] = timeString.split(':');
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return;
    }

    const baseDate = selectedDate
      ? dayjs(selectedDate)
      : beat.dates_publish.length
        ? dayjs(beat.dates_publish[0])
        : null;

    if (baseDate) {
      const newDate = baseDate.set('hour', hour).set('minute', minute);
      setSelectedDate(newDate.toISOString());
    } else {
      console.error('No dates!');
    }
  };

  const updatePublishDates = () => {
    mutate();
    // acceptInvite();
    // eslint-disable-next-line no-console
    // console.log(collabId);
  };

  const products = Object.entries(beat.products || {}).map(([key, value]) => `${key}: ${value}`);
  const isIGCP = ['IGCP', 'ORGPOST'].includes(beat.beat_type || '');

  return (
    <Modal modalStyles={{ maxWidth: '480px' }} handleClose={onCancel}>
      <div className={styles.welcomeModal}>
        <h2 className={`${styles.welcomeModalTitle} ${styles.BottomMarginSm}`}>
          {t('inviteView.chooseDateModal.title')}
        </h2>
        <div className={styles.welcomeModalDescription}>
          {t('inviteView.chooseDateModal.description')}
        </div>
        <div className={styles.welcomeModalBody}>
          {beat.dates_publish?.length && isIGCP && (
            <div key={`beat-date-publish-${beat.beat_name}`} className={styles.inviteViewBeat}>
              <div className={styles.inviteViewBeatHeader}>
                <div className={styles.inviteViewBeatHeaderTitle}>
                  {PlatformIcon(beat.platform)}
                  <span>{beat.beat_name}</span>
                </div>
                {products.map((p) => (
                  <div key={p.replace(/\s+/g, '')} className={styles.inviteViewBeatItem}>
                    {p.replace(/_/g, ' ')}
                  </div>
                ))}
              </div>
              <div className={styles.inviteViewBeatBody}>
                <div className={styles.inviteViewBeatBodyDate}>
                  <SelectInput
                    value={selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : null}
                    onChange={handleDateChange}
                    options={beat.dates_publish.map((date: string) => ({
                      value: dayjs(date).format('YYYY-MM-DD'),
                      label: dayjs(date).format('YYYY-MM-DD')
                    }))}
                  />
                </div>
                <div className={styles.inviteViewBeatBodyTime}>
                  <TimeInput
                    value={dayjs(selectedDate).format('HH:mm') || '00:00'}
                    onChange={(e) => handleTimeChange(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={styles.welcomeModalButton}>
          <Button
            theme="cta"
            onClick={updatePublishDates}
            isLoading={isLoading}
            disabled={!selectedDate}
          >
            {t('actions.update')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default ChangeDatesModal;
